// src/app/modules/Devices/pages/DevicesPage.jsx
// Fingerprint terminals (Sety Checkpoint) for the active organization.

import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Fingerprint, MapPin, Clock, Cpu, RefreshCw, AlertTriangle, ListChecks, ScanLine, Wifi,
} from 'lucide-react';
import { devicesApi, errorMessage } from '../api';
import { useDeviceAccess, usePolling, useEvent, lastSeen, HEALTH_FLAGS, signal } from '../utils';
import { Pill, Stat, EmptyState, Button } from '../components/ui';
import '../devices.scss';

const REFRESH_MS = 15000;

function DeviceCard({ device }) {
  const hb = device.last_heartbeat || {};
  const rssi = signal(hb.wifi_rssi);
  const flags = (device.health_flags || []).map((f) => HEALTH_FLAGS[f]).filter(Boolean);

  return (
    <Link to={`/devices/${encodeURIComponent(device.device_id)}`} className="dv-device">
      <div className="dv-device__top">
        <span className="dv-device__icon"><Fingerprint size={20} /></span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <h3 className="dv-device__name">{device.name || device.device_id}</h3>
          <div className="dv-device__id">{device.device_id}</div>
        </div>
        {device.status !== 'active' && <Pill tone="danger">{device.status}</Pill>}
        {device.registration_open && <Pill tone="brand">Enrolling</Pill>}
      </div>

      <div className="dv-device__meta">
        <span className="dv-device__meta-row"><MapPin size={13} />{device.location_name || 'No location set'}</span>
        <span className="dv-device__meta-row"><Clock size={13} />Last seen {lastSeen(device.last_seen_at)}</span>
        <span className="dv-device__meta-row">
          <Cpu size={13} />Firmware {device.firmware_version || '—'}
          {hb.wifi_rssi != null && <><span style={{ opacity: 0.4 }}>·</span><Wifi size={13} />{rssi.label.split(' · ')[0]}</>}
        </span>
      </div>

      <div className="dv-device__foot">
        <Pill tone="brand">{device.stats?.enrolled_fingerprints || 0} fingerprints</Pill>
        {device.stats?.pending_commands > 0 && (
          <Pill tone={device.stats.failing_commands ? 'danger' : 'warning'}>{device.stats.pending_commands} pending</Pill>
        )}
        {device.stats?.events_needing_review > 0 && <Pill tone="danger">{device.stats.events_needing_review} to review</Pill>}
        {flags.map((f) => <Pill key={f.label} tone={f.tone}>{f.label}</Pill>)}
      </div>
    </Link>
  );
}

export function DevicesPage() {
  const { orgId, locations } = useDeviceAccess();
  const [devices, setDevices] = useState(null);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [locationId, setLocationId] = useState('');
  const [search, setSearch] = useState('');

  const load = useEvent(async (manual = false) => {
    if (!orgId) return;
    if (manual) setRefreshing(true);
    try {
      const res = await devicesApi.list(orgId);
      setDevices(res.devices);
      setError(null);
    } catch (err) {
      setError(errorMessage(err, 'Could not load devices'));
    } finally {
      setRefreshing(false);
    }
  });
  usePolling(load, REFRESH_MS, !!orgId);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (devices || []).filter((d) => (!locationId || d.location_id === locationId)
      && (!q || [d.name, d.device_id, d.location_name].some((v) => (v || '').toLowerCase().includes(q))));
  }, [devices, locationId, search]);

  const totals = useMemo(() => (devices || []).reduce((t, d) => ({
    fingerprints: t.fingerprints + (d.stats?.enrolled_fingerprints || 0),
    pending: t.pending + (d.stats?.pending_commands || 0),
    review: t.review + (d.stats?.events_needing_review || 0),
    attention: t.attention + ((d.health_flags || []).length > 0 || d.status !== 'active' ? 1 : 0),
  }), { fingerprints: 0, pending: 0, review: 0, attention: 0 }), [devices]);

  return (
    <div className="dv-page">
      <div className="dv-header">
        <div>
          <h1>Devices</h1>
          <p>Sety Checkpoint fingerprint terminals — staff clock in and out with a fingerprint.</p>
        </div>
        <div className="dv-header__actions">
          <Button icon={RefreshCw} loading={refreshing} onClick={() => load(true)}>Refresh</Button>
        </div>
      </div>

      {devices && devices.length > 0 && (
        <div className="dv-stats">
          <Stat icon={Fingerprint} label="Devices" value={devices.length} />
          <Stat icon={ScanLine} tone="success" label="Enrolled fingerprints" value={totals.fingerprints} />
          <Stat icon={ListChecks} tone={totals.pending ? 'warning' : 'success'} label="Pending commands" value={totals.pending} />
          <Stat icon={AlertTriangle} tone={totals.review ? 'danger' : 'success'} label="Scans to review" value={totals.review} />
        </div>
      )}

      {devices && devices.length > 1 && (
        <div className="dv-toolbar">
          <input type="search" placeholder="Search devices…" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search devices" />
          {locations.length > 0 && (
            <select value={locationId} onChange={(e) => setLocationId(e.target.value)} aria-label="Filter by location">
              <option value="">All locations</option>
              {locations.map((l) => <option key={l.location_id} value={l.location_id}>{l.name}</option>)}
            </select>
          )}
        </div>
      )}

      {error && <div className="dv-notice dv-notice--danger"><AlertTriangle size={16} />{error}</div>}

      {!devices && !error && (
        <div className="dv-grid">{[0, 1, 2].map((i) => <div key={i} className="dv-skeleton" />)}</div>
      )}

      {devices && devices.length === 0 && (
        <div className="dv-card">
          <EmptyState icon={Fingerprint} title="No devices yet">
            Fingerprint terminals are registered by Sety and assigned to your organization.
            Contact Sety support to get a Checkpoint device set up for your sites.
          </EmptyState>
        </div>
      )}

      {devices && devices.length > 0 && visible.length === 0 && (
        <div className="dv-card"><EmptyState icon={Fingerprint} title="No matching devices" /></div>
      )}

      {visible.length > 0 && (
        <div className="dv-grid">{visible.map((d) => <DeviceCard key={d.device_id} device={d} />)}</div>
      )}
    </div>
  );
}

export default DevicesPage;
